<template>
  <EChartCard
    :title="title"
    :subtitle="$t('recordsCounted')"
    :buildOption="buildOption"
    :height="380"
    @chart-click="handleChartClick"
  />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import EChartCard from '@/components/charts/base/EChartCard.vue';
import { getCategorical, statusColors } from '@/utils/vizPalette';
import { axisColor, DIM_OPACITY } from '@/utils/chartTheme';
import { useSelectableFilter } from '@/composables/useSelectableFilter';
import { sqlLiteral } from '@/utils/assetFields';

const props = defineProps({
  // Which dimension the inspection records are grouped by.
  groupBy: {
    type: String,
    required: true,
    validator: (value) => ['drawing', 'category', 'type'].includes(value),
  },
});

const store = useStore();
const { t } = useI18n();

// One filter slot per instance, so the three charts intersect rather than
// overwrite each other.
const chartName = computed(() => `plannedInspected_${props.groupBy}`);
const { isSelected, hasSelection, sync, toggle } = useSelectableFilter(chartName.value);

const TITLE_KEYS = {
  drawing: 'plannedInspectedByDrawing',
  category: 'plannedInspectedByCategory',
  type: 'plannedInspectedByType',
};
const title = computed(() => t(TITLE_KEYS[props.groupBy]));

// Counts are over ITEMS (lifecycle records), not unique assets:
//   Total     every record in scope
//   Planned   record carries a next inspection date
//   Inspected record is closed
const GROUPS = {
  drawing: {
    id: 'd.Id',
    label: 'd.Title',
    join: `
      LEFT JOIN locations l ON l.Id = i.locationId
      LEFT JOIN drawings d ON d.Id = l.DrawingId`,
    condition: (id) => `assetLocationId IN (SELECT Id FROM locations WHERE DrawingId = ${sqlLiteral(id)})`,
    labelKey: 'drawing',
  },
  category: {
    id: 'i.assetCategory',
    label: 'i.assetCategory',
    join: '',
    condition: (id) => `assetCategory = ${sqlLiteral(id)}`,
    labelKey: 'category',
  },
  type: {
    id: 'i.assetType',
    label: 'i.assetType',
    join: '',
    condition: (id) => `assetType = ${sqlLiteral(id)}`,
    labelKey: 'assetType',
  },
};

const rows = ref([]);

const buildOption = async () => {
  sync();
  const conn = store.getters.getDBConnection;
  if (!conn) return null;

  const group = GROUPS[props.groupBy];
  const result = await conn.query(`
    SELECT
      ${group.id} AS groupId,
      ${group.label} AS groupLabel,
      COUNT(*) AS total,
      COUNT(*) FILTER (WHERE i.nextInspectionDate IS NOT NULL) AS planned,
      COUNT(*) FILTER (WHERE i.isClosed) AS inspected
    FROM filteredItems i
    ${group.join}
    WHERE ${group.id} IS NOT NULL
    GROUP BY 1, 2
    ORDER BY total ASC
  `);

  // Ascending, so the biggest group sits at the top of a category yAxis.
  const data = result.toArray().map((row) => {
    const json = row.toJSON();
    return {
      id: json.groupId,
      label: json.groupLabel,
      total: Number(json.total) || 0,
      planned: Number(json.planned) || 0,
      inspected: Number(json.inspected) || 0,
    };
  });
  if (data.length === 0) return null;
  rows.value = data;

  const fontColor = store.getters.chartFontColor;
  const gridColor = axisColor(store.state.theme);
  const palette = getCategorical(store.state.theme);

  const series = [
    { key: 'total', name: t('total'), color: statusColors.neutral },
    { key: 'planned', name: t('planned'), color: palette[0] },
    { key: 'inspected', name: t('inspected'), color: statusColors.good },
  ].map((spec) => ({
    name: spec.name,
    type: 'bar',
    barMaxWidth: 12,
    // The legend swatch reads the SERIES colour; without it the legend would
    // show the default palette while the bars use the per-datum colours.
    color: spec.color,
    itemStyle: { color: spec.color, borderRadius: [0, 3, 3, 0] },
    data: data.map((row) => ({
      value: row[spec.key],
      itemStyle: {
        color: spec.color,
        borderRadius: [0, 3, 3, 0],
        opacity: hasSelection.value && !isSelected(row.id) ? DIM_OPACITY : 1,
      },
    })),
  }));

  // Keep bar heights readable when there are many groups: show a window and
  // let the user scroll the axis.
  const VISIBLE = 12;
  const needsZoom = data.length > VISIBLE;

  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: {
      top: 0,
      left: 'center',
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: fontColor },
    },
    grid: { left: 8, right: needsZoom ? 40 : 24, top: 36, bottom: 4, containLabel: true },
    xAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: fontColor },
      splitLine: { lineStyle: { color: gridColor } },
    },
    yAxis: {
      type: 'category',
      data: data.map((row) => row.label),
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: { color: fontColor, overflow: 'truncate', width: 130 },
    },
    dataZoom: needsZoom
      ? [
        { type: 'inside', yAxisIndex: 0, startValue: data.length - VISIBLE, endValue: data.length - 1, zoomOnMouseWheel: false, moveOnMouseWheel: true },
        { type: 'slider', yAxisIndex: 0, startValue: data.length - VISIBLE, endValue: data.length - 1, width: 10, right: 6, showDetail: false, brushSelect: false },
      ]
      : undefined,
    series,
  };
};

const handleChartClick = async (params) => {
  const row = rows.value[params?.dataIndex];
  if (!row) return;

  const group = GROUPS[props.groupBy];
  await toggle(row.id, group.condition(row.id), `${t(group.labelKey)}: ${row.label}`);
};
</script>
